import { useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Alert } from './ui/Alert';
import { Sheet, SheetContent, SheetBody, SheetFooter } from './ui/Sheet';
import { StepUpCodeField } from './StepUpCodeField';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from '../lib/authErrors';
import {
  STEP_UP_ENDED_MESSAGE,
  isStepUpRequired,
  stepUpBody,
  stepUpCodeReady,
  useStepUpWindow,
} from '../lib/stepUp';
import {
  ApiError,
  type BackInStockPreview,
  type BackInStockSettings,
  type BackInStockStatus,
} from '../types/api';

const QUERY_KEY = ['back-in-stock'];

function errorText(err: unknown): string {
  if (isTotpLocked(err)) return TOTP_LOCKED_MESSAGE;
  if (isStepUpRequired(err)) return STEP_UP_ENDED_MESSAGE;
  if (err instanceof ApiError && err.status === 401)
    return 'The code is incorrect or has already been used.';
  return 'The setting could not be saved. Try again.';
}

function SwitchSheet({
  open,
  onOpenChange,
  enable,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  enable: boolean;
}) {
  const [totpCode, setTotpCode] = useState('');
  const queryClient = useQueryClient();
  const stepUp = useStepUpWindow();
  const codeId = useId();

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<BackInStockSettings>('/back-in-stock', {
        method: 'POST',
        body: { enabled: enable, ...(stepUp.active ? {} : stepUpBody(totpCode)) },
      }),
    onError: stepUp.onStepUpError,
    onSuccess: () => {
      if (!stepUp.active) stepUp.onStepUpSuccess();
      onOpenChange(false);
      setTotpCode('');
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
  const canSubmit = (stepUp.active || stepUpCodeReady(totpCode)) && !mutation.isPending;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) mutation.reset();
      }}
    >
      {open ? (
        <SheetContent
          eyebrow="Automations"
          title={enable ? 'Turn on: back in stock' : 'Turn off: back in stock'}
          description={
            enable
              ? 'The shop will show an email form on sold-out products, and one service mail goes out when the product is back. Requires a code from the authenticator app (not if you entered one in the last 30 minutes).'
              : 'The form stops accepting requests and no more mails go out. Waiting requests are kept until they expire. Requires a code from the authenticator app (not if you entered one in the last 30 minutes).'
          }
        >
          <form
            id="back-in-stock-switch-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) mutation.mutate();
            }}
          >
            <SheetBody>
              {mutation.isError ? <Alert>{errorText(mutation.error)}</Alert> : null}
              <StepUpCodeField
                id={codeId}
                value={totpCode}
                onChange={setTotpCode}
                active={stepUp.active}
                minutesLeft={stepUp.minutesLeft}
              />
            </SheetBody>
            <SheetFooter>
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={!canSubmit}>
                {mutation.isPending ? 'Saving…' : enable ? 'Turn on' : 'Turn off'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      ) : null}
    </Sheet>
  );
}

/** The preview's sample product (routes/backInStock.ts), for the "Shows as" line. */
const SAMPLE_PRODUCT = 'NoPain® Numbing Cream';
const PRODUCT_TOKEN = '{product}';

/**
 * A text field where the product name is added with a button instead of
 * typing `{product}` (Mikkel, 27 Oct 2026: nobody remembers the exact
 * product name or the placeholder). The button puts it at the cursor, and
 * a line below shows the text with a sample product.
 */
function ProductTextField({
  id,
  label,
  value,
  maxLength,
  placeholder,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  maxLength: number;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const insertProduct = () => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const token =
      (before && !/\s$/.test(before) ? ' ' : '') +
      PRODUCT_TOKEN +
      (after && !/^\s/.test(after) ? ' ' : '');
    const next = before + token + after;
    if (next.length > maxLength) return;
    onChange(next);
    const caret = before.length + token.length;
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(caret, caret);
    });
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-[13px]" onClick={insertProduct}>
          + Product name
        </Button>
      </div>
      <Input
        ref={inputRef}
        id={id}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {value.includes(PRODUCT_TOKEN) ? (
        <div className="text-xs text-muted">
          Shows as: {value.split(PRODUCT_TOKEN).join(SAMPLE_PRODUCT)}
        </div>
      ) : null}
    </div>
  );
}

function TextForm({ settings }: { settings: BackInStockSettings }) {
  const queryClient = useQueryClient();
  const subjectId = useId();
  const bodyId = useId();
  const [subject, setSubject] = useState(settings.subject ?? '');
  const [bodyText, setBodyText] = useState(settings.bodyText ?? '');

  const save = useMutation({
    mutationFn: () =>
      apiFetch<BackInStockSettings>('/back-in-stock', {
        method: 'POST',
        body: { subject: subject.trim() || null, bodyText: bodyText.trim() || null },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
  const preview = useQuery({
    queryKey: ['back-in-stock-preview', settings.updatedAt],
    queryFn: () => apiFetch<BackInStockPreview>('/back-in-stock/preview?language=en'),
  });

  return (
    <div className="flex flex-col gap-3">
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <ProductTextField
          id={subjectId}
          label="Subject (optional)"
          value={subject}
          maxLength={200}
          placeholder="Leave empty for the built-in subject"
          onChange={setSubject}
        />
        <ProductTextField
          id={bodyId}
          label="Intro text (optional)"
          value={bodyText}
          maxLength={1000}
          placeholder="Leave empty for the built-in text"
          onChange={setBodyText}
        />
        {save.isError ? <Alert>The texts could not be saved. Try again.</Alert> : null}
        <div className="flex justify-end">
          <Button type="submit" variant="secondary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save texts'}
          </Button>
        </div>
      </form>
      {preview.data ? (
        <div className="flex flex-col gap-1">
          <div className="text-xs text-muted">Preview, subject: {preview.data.subject}</div>
          <iframe
            title="Back in stock mail preview"
            sandbox=""
            srcDoc={preview.data.html}
            className="w-full h-[360px] border border-border rounded-lg bg-white"
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * "Back in stock" service mail (migration 0050, docs/decisions.md). Not a
 * flow: one single-purpose mail per request. Admin switches it; everyone can
 * read the waiting counts (no addresses are ever shown).
 */
export function BackInStockSection() {
  const { isAdmin } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiFetch<BackInStockStatus>('/back-in-stock'),
  });

  return (
    <Card className="p-4 flex flex-col gap-3">
      <h2 className="text-sm font-semibold m-0">Back in stock</h2>
      <p className="m-0 text-sm text-muted">
        Visitors can leave their email on a sold-out product. When it is back in stock they get one
        service mail with a link, and the address is then deleted after 90 days. It is not a
        newsletter and does not use the flows.
      </p>

      {isLoading ? <div className="text-muted text-sm">Loading status…</div> : null}
      {isError ? <Alert>Status could not be loaded.</Alert> : null}

      {data ? (
        <>
          <div className="text-sm">
            Status:{' '}
            <span className={data.settings.enabled ? 'text-success-text' : 'text-muted'}>
              {data.settings.enabled ? 'On' : 'Off'}
            </span>
            {' · '}
            {data.pendingTotal} waiting
          </div>

          {data.pending.length > 0 ? (
            <ul
              className="m-0 p-0 list-none text-sm max-w-[480px]"
              aria-label="Waiting per product"
            >
              {data.pending.map((p) => (
                <li
                  key={`${p.productId}-${p.variationId}`}
                  className="flex justify-between py-1 border-b border-border-soft"
                >
                  <span>{p.productName}</span>
                  <span>{p.pending}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {isAdmin ? (
            <>
              <div className="flex justify-end">
                <Button variant="secondary" onClick={() => setSheetOpen(true)}>
                  {data.settings.enabled ? 'Turn off' : 'Turn on'}
                </Button>
              </div>
              <TextForm key={data.settings.updatedAt} settings={data.settings} />
              <SwitchSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                enable={!data.settings.enabled}
              />
            </>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}
